import { atom } from 'jotai';

export interface DeviationTracker {
  toastId: string | number;
  batchId: string;
  projectId: string;
  sourceLayerId: string;
  sourceLayerName: string;
  /** Snapshot of layer IDs that were already derived from `sourceLayerId` at
   * the moment the user clicked Run. The "new layer" produced by this run is
   * any layer with `metadata.derivedFrom === sourceLayerId` whose ID is NOT
   * in this snapshot. We previously compared `layer.createdAt` (server clock)
   * to a `Date.now()` captured client-side, which fails under any clock skew
   * — toast would either spin forever (client ahead of server) or resolve
   * immediately by matching a stale pre-existing derived layer (client behind). */
  existingDerivedLayerIds: string[];
  /** Which analysis produced this tracker. Picks the toast strings used on
   * completion / failure; `'deviation'` and `'surfaceDifference'` share the
   * same matching logic (both resolve on a new layer whose
   * `metadata.derivedFrom` is the source layer id). Defaults to
   * `'deviation'` when absent.
   *
   * `'clearance'` is the CLEARANCE run started from `RunClearanceDialog`. It
   * rides this same tracker, but its resolution is STRICTER than the other
   * two: postprocess stamps the very same `metadata.derivedFrom` on every
   * analysis output of a given cloud, so `derivedFrom` + the snapshot alone
   * resolved a clearance toast against ANOTHER run's layer while this run was
   * still on ECS. `useDeviationProgress` therefore additionally requires
   * `metadata.batchId === batchId` and this batch's POST (postprocess) job to
   * be PROCESSED before it calls a clearance run complete.
   *
   * `'layerStack'` is the LAYER_STACK run started from `RunLayerStackDialog`.
   * It resolves on `metadata.batchId` ALONE, never on `derivedFrom`: a stack
   * has N source surfaces and no single "source layer" to derive from, and
   * `postprocess.ts` stamps no `derivedFrom` on the ONE result layer it mints
   * (spec §6.2). Like clearance it also waits for this batch's POST job to be
   * PROCESSED, so the toast resolves when postprocess is finished rather than
   * when it has merely inserted the row.
   *
   * `'sectionOverlays'` is the SECTION_OVERLAYS re-sample started from the
   * dock's Overlays popover. It is the odd one out: it mints NO layer at all —
   * it rewrites two byproducts on the RUN's existing batch and stamps
   * `metadata.sectionOverlays` back onto the same result layer. So it resolves
   * on its own job's status alone (`type === 'SECTION_OVERLAYS'` in
   * `tracker.batchId`), and the layer scan below never runs for it.
   *
   * `'map2d'` — a MAP_2D regeneration from the layer menu; resolves on its own job, mints no layer. */
  kind?: 'deviation' | 'surfaceDifference' | 'clearance' | 'layerStack' | 'sectionOverlays' | 'map2d';
  /**
   * `'sectionOverlays'` only: the batch the OVERLAYS were sampled onto — the
   * original run's batch, not the SECTION_OVERLAYS batch in `batchId`.
   *
   * The two are different batches, and the chart reads the sampled file
   * through `useByproductUrl(runBatchId, …)`. Without invalidating that key on
   * completion the new file is registered server-side and the open dock keeps
   * showing the previous one — for as long as the tab stays open, since
   * `batchByproducts` is cached with no stale time.
   */
  runBatchId?: string;
}

export const deviationTrackingAtom = atom<DeviationTracker[]>([]);
